import { Countries } from "../../utils/Countries"
import {
  AddressDetailsResp,
  PlaceType,
} from "../components/steps/CheckoutShippingAddress"
import { FormActionsType, FormStateType } from "../../contexts/formContext"

class CheckoutShippingAddressEvents {
  static autocompleteService = { current: null }

  static handleChange(
    event: React.ChangeEvent<HTMLInputElement>,
    type: "shipping" | "billing",
    formActions: FormActionsType,
    onChange: (() => void) | undefined,
    setAutoCompleteOptions: React.Dispatch<React.SetStateAction<PlaceType[]>>,
    formState: FormStateType
  ) {
    let data: any = {}
    data[`${type}Street`] = event.target.value
    formActions.setFormData(data)
    this.updatePlaces(
      event.target.value,
      setAutoCompleteOptions,
      formState,
      type
    )

    if (onChange) {
      onChange()
    }
  }

  static fetchPlaces(
    input: { input: string; componentRestrictions?: { country?: string } },
    callback: (results?: PlaceType[]) => void
  ) {
    ;(this.autocompleteService.current as any).getPlacePredictions(
      input,
      callback
    )
  }

  static updatePlaces(
    inputValue: string,
    setAutocompleteOptions: React.Dispatch<React.SetStateAction<PlaceType[]>>,
    formState: FormStateType,
    type: "shipping" | "billing"
  ) {
    let active = true

    if (!this.autocompleteService.current && (window as any).google) {
      this.autocompleteService.current = new (window as any).google.maps.places.AutocompleteService()
    }
    if (!this.autocompleteService.current) {
      return undefined
    }

    if (inputValue === "") {
      setAutocompleteOptions([])
      return undefined
    }
    const countryField = formState.inputs[`${type}Country`]
    const country = countryField.value || ""
    const countryCode = Countries.get(country)?.code || ""
    this.fetchPlaces(
      {
        input: inputValue,
        ...(country && { componentRestrictions: { country: countryCode } }),
      },
      (results?: PlaceType[]) => {
        if (active) {
          setAutocompleteOptions(results || [])
        }
      }
    )
  }

  static async getPlacesPostCodeById(
    placeId: string
  ): Promise<AddressDetailsResp> {
    return new Promise<AddressDetailsResp>((resolve, reject) => {
      try {
        new (window as any).google.maps.places.PlacesService(
          document.createElement("div")
        ).getDetails(
          {
            placeId,
            fields: ["address_components"],
          },
          (details: {
            address_components: Array<{
              long_name: string
              short_name: string
              types: string[]
            }>
          }) => {
            return resolve(details)
          }
        )
      } catch (e) {
        reject(e)
      }
    })
  }

  static handleSelectAddress(
    option: any,
    type: "shipping" | "billing",
    formActions: FormActionsType
  ) {
    return async () => {
      const addressDetails = await this.getPlacesPostCodeById(option.place_id)
      let data: any = {}
      data[`${type}Street`] = "" //option.structured_formatting.main_text //start empty as we will populate as we go
      let streetCounter = 0
      addressDetails.address_components.forEach(c => {
        switch (c.types[0]) {
          case "country":
            const country = c.long_name
            let mappedCountry =
              country === "USA"
                ? "United States"
                : country === "UK"
                ? "United Kingdom"
                : country
            if (Countries.get(mappedCountry)) {
              data[`${type}Country`] = mappedCountry
            } else {
              data[`${type}Country`] = ""
            }
            break
          case "administrative_area_level_1":
            data[`${type}Province`] = c.long_name
            break
          case "administrative_area_level_2":
            if (!data[`${type}City`]) {
              data[`${type}City`] = c.long_name
            }
            break
          case "locality":
          case "postal_town":
          case "sublocality":
            data[`${type}City`] = data[`${type}City`]
              ? data[`${type}City`] + " " + c.long_name
              : c.long_name
            break
          case "postal_code":
            data[`${type}Postal`] = c.long_name
            break
          case "premise":
          case "subpremise":
            data[`${type}Unit`] = c.long_name
            break
          case "route":
          case "natural_feature":
          case "street_address":
          case "street_number":
            const value =
              c.types[0] === "route" || c.types[0] === "street_address"
                ? c.short_name
                : c.long_name
            data[`${type}Street`] =
              streetCounter == 0
                ? c.long_name
                : data[`${type}Street`] + " " + value
            streetCounter++
            break
        }
      })

      formActions.setFormData(data)

      const input = window.document.getElementById(
        "sensai-address"
      ) as HTMLInputElement
      if (input) {
        input.value = data[`${type}Street`] || ""
      }
    }
  }

  static handleKeyDown(
    e: React.KeyboardEvent,
    type: "shipping" | "billing",
    formActions: FormActionsType
  ) {
    if (window && e.key === "Enter") {
      const input = window.document.getElementById(
        "sensai-address"
      ) as HTMLInputElement
      if (input.value.split(",").length == 4) {
        this.handleSelectAddress(
          {
            description: input.value,
          },
          type,
          formActions
        )()
      }
    }
  }

  static handleOnFocus(event: React.FocusEvent<HTMLInputElement>) {
    if (event.target.autocomplete) {
      event.target.autocomplete = "disable"
    }
  }

  static handleOnBlur(formState: FormStateType, type: "shipping" | "billing") {
    setTimeout(() => {
      const input = window.document.getElementById(
        "sensai-address"
      ) as HTMLInputElement
      if (input) {
        input.value = formState.inputs[`${type}Street`]?.value || ""
      }
    }, 1000)
  }
}

export default CheckoutShippingAddressEvents
